import { useTranslation } from 'react-i18next';

import type { UnaddressedMailingPositionHolidayAdjustmentDto } from '@/services/ws-post/src/main/resources/rest-api';
import { fromDate } from '@/services/ws-post/src/main/resources/rest-api';
import type { DateType } from '@/js/libs/api-helpers';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';

import { DATE_FORMAT } from 'src/constants.ts';

type HolidayAdjustmentsNotifier = (adjustments: UnaddressedMailingPositionHolidayAdjustmentDto[] | undefined) => void;

const formatDate = (value: DateType): string => fromDate(value).format(DATE_FORMAT);

/**
 * Shows a warning toast per mailing-position adjustment PromoPost applied. A position can both have
 * its distribution date shifted and carry postcodes delivered later because of a regional holiday,
 * so the two cases are reported independently.
 */
export const useHolidayAdjustmentsNotification = (): HolidayAdjustmentsNotifier => {
  const [t] = useTranslation(['postBookingTool', 'common']);
  const notify = useNotification();

  return useCallbackRef<HolidayAdjustmentsNotifier>((adjustments) => {
    if (!adjustments?.length) {
      return;
    }

    const warn = (body: string): void => {
      notify.warn({
        title: t('common:WARNING'),
        body,
      });
    };

    adjustments.forEach((adjustment) => {
      if (adjustment.distributionDate && adjustment.previousDistributionDate) {
        warn(
          t('postBookingTool:MAILING_HOLIDAY_DATE_MOVED', {
            positionName: adjustment.positionName,
            previous: formatDate(adjustment.previousDistributionDate),
            new: formatDate(adjustment.distributionDate),
            interpolation: { escapeValue: false },
          }),
        );
      }

      if (adjustment.holidayAffectedPostalCodes.length > 0) {
        warn(
          t('postBookingTool:MAILING_HOLIDAY_AFFECTED_POSTCODES', {
            positionName: adjustment.positionName,
            postcodes: adjustment.holidayAffectedPostalCodes.join(', '),
            interpolation: { escapeValue: false },
          }),
        );
      }
    });
  });
};
